import { Button, Form } from 'react-bootstrap';
import { ClientSelected } from 'modules/client';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import spinnerTitle from 'components/others/spinnerTitle';
import {
    PHONE_INPUT_MAX_LENGTH,
    isSanitizedPhoneValid,
    sanitizePhoneInput,
} from 'utils/helpers/functions/sanitizePhoneInput';
import SpinnerBlock from 'components/others/SpinnerBlock';
import ETiny from 'components/editors/ETiny';
import Editor from 'components/editors/Editor';
import conf from 'conf/conf';
import { LEAD_NAME_MAX_LENGTH, type ClientType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';

const EXISTING_CLIENT_STORAGE_KEY = 'econtractor.lead.new.existingClient';

const readExistingClient = (): boolean => {
    try {
        return window.localStorage.getItem(EXISTING_CLIENT_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
};

interface NewLeadFormProps {
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    isSubmitting: boolean;
    setFieldValue: (field: string, value: unknown) => void;
    values: any;
    isValidPhone: boolean;
    setIsValidPhone: (valid: boolean) => void;
}

const NewLeadForm = ({
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    isValidPhone,
    setIsValidPhone,
}: NewLeadFormProps) => {
    const { t } = useTranslation(['Leads', 'Clients', 'common']);
    const refEdit = useRef<any>(null);
    const refLeadName = useRef<HTMLInputElement | null>(null);
    const TinyEditor = (conf.tinyMode ? ETiny : Editor) as any;
    const [selectedClient, setSelectedClient] = useState<ClientType | null>(null);
    const [existingClient, setExistingClient] = useState(readExistingClient);

    const clearClientFields = () => {
        setSelectedClient(null);
        setFieldValue('clientId', 0);
        setFieldValue('clientName', '');
        setFieldValue('clientPhone', '');
        setFieldValue('clientEmail', '');
        setFieldValue('clientAddress', '');
        setFieldValue('clientLocation', '');
        setIsValidPhone(false);
    };

    const focusName = () => {
        const inputClient = document.querySelector('input[name="clientName"]') as HTMLInputElement | null;
        if (inputClient) inputClient.focus();
    };

    const focusClientSearch = () => {
        const inputSearch = document.querySelector(
            '.off-new-lead-form .user-selected-search input',
        ) as HTMLInputElement | null;
        if (inputSearch) inputSearch.focus();
    };

    const handleChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cleaned = sanitizePhoneInput(e.target.value);

        if (values.clientPhone !== cleaned) {
            setFieldValue('clientPhone', cleaned);
        }

        const isValid = isSanitizedPhoneValid(cleaned);
        if (isValidPhone !== isValid) {
            setIsValidPhone(isValid);
        }
    };

    const handleChangeClient = (client: ClientType | null) => {
        setSelectedClient(client);

        if (!client) {
            setFieldValue('clientId', 0);
            setFieldValue('clientName', '');
            setFieldValue('clientPhone', '');
            setFieldValue('clientEmail', '');
            setFieldValue('clientAddress', '');
            setFieldValue('clientLocation', '');
            setIsValidPhone(false);
            return;
        }

        setFieldValue('clientId', client.ID);
        setFieldValue('clientName', client.clientName || '');
        setFieldValue('clientPhone', client.clientPhone || '');
        setFieldValue('clientEmail', client.clientEmail || '');
        setFieldValue('clientAddress', client.clientAddress || '');
        setFieldValue('clientLocation', client.clientLocation || '');
        handleChangePhone({
            target: { value: client.clientPhone || '' },
        } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleExistingClientChange = (checked: boolean) => {
        setExistingClient(checked);
        try {
            window.localStorage.setItem(EXISTING_CLIENT_STORAGE_KEY, checked ? '1' : '0');
        } catch {
            /* ignore quota / private mode */
        }
        clearClientFields();
    };

    useEffect(() => {
        if (existingClient) focusClientSearch();
        else focusName();
    }, [existingClient]);

    useEffect(() => {
        if (!existingClient || !selectedClient) return;
        refLeadName.current?.focus();
    }, [existingClient, selectedClient]);

    return (
        <Form className="d-flex flex-column h-100" onSubmit={handleSubmit}>
            <div className="off-new-lead-form d-flex flex-column h-100">
                <div className="off-new-lead-content px-3 position-relative">
                    <SpinnerBlock show={isSubmitting} onlyBlur={true} />

                    <div className="form-check form-switch mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            role="switch"
                            id="new-lead-existing-client"
                            checked={existingClient}
                            onChange={(e) => handleExistingClientChange(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="new-lead-existing-client">
                            {t('newLead.existingClient')}
                        </label>
                        <div className="small text-muted">{t('newLead.existingClientHint')}</div>
                    </div>

                    {existingClient ? (
                        <>
                            <ClientSelected
                                title=""
                                value={selectedClient}
                                autoFocus
                                onChange={handleChangeClient}
                            />
                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('newLead.leadName')}</label>
                                <Field
                                    innerRef={refLeadName}
                                    name="leadName"
                                    maxLength={LEAD_NAME_MAX_LENGTH}
                                    className="form-control"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        setFieldValue('leadName', e.target.value.slice(0, LEAD_NAME_MAX_LENGTH));
                                    }}
                                />
                            </Form.Group>
                        </>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('Clients:labelName')}</label>
                                <Field required id="newLeadClientName" name="clientName" className="form-control" />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('newLead.leadName')}</label>
                                <Field
                                    name="leadName"
                                    maxLength={LEAD_NAME_MAX_LENGTH}
                                    className="form-control"
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        setFieldValue('leadName', e.target.value.slice(0, LEAD_NAME_MAX_LENGTH));
                                    }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <div className="d-flex justify-content-between">
                                    <label className="form-label text-muted">{t('Clients:labelPhone')}</label>

                                    {values?.clientPhone && values?.clientPhone !== '+' && (
                                        <div className="pe-1 position-relative d-flex align-items-start">
                                            <span
                                                className={`badge bg-light-${isValidPhone ? 'success' : 'danger'}`}
                                                style={{ height: 'fit-content', paddingBottom: '2px' }}
                                            >
                                                {isValidPhone ? t('Clients:phoneValid') : t('Clients:phoneNotValid')}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <Field
                                    name="clientPhone"
                                    maxLength={PHONE_INPUT_MAX_LENGTH}
                                    onChange={handleChangePhone}
                                    className="form-control"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('Clients:labelEmail')}</label>
                                <Field name="clientEmail" className="form-control" />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('Clients:labelAddress')}</label>
                                <Field name="clientAddress" className="form-control" />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <label className="form-label text-muted">{t('Clients:labelLocation')}</label>
                                <Field name="clientLocation" className="form-control" />
                            </Form.Group>
                        </>
                    )}

                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">{t('newLead.leadInfo')}</label>
                        <TinyEditor
                            ref={refEdit}
                            value={values?.eventInfo || ''}
                            onChange={(newValue: string) => {
                                setFieldValue('eventInfo', newValue);
                            }}
                        />
                    </Form.Group>
                </div>

                <div className="p-3 offcanvas-footer off-new-lead-footer">
                    <div className="d-flex justify-content-end">
                        <Button
                            type="submit"
                            variant="info"
                            disabled={isSubmitting || (existingClient && !values.clientId)}
                            className="min100px"
                        >
                            {spinnerTitle(
                                <>
                                    <SolarIcon name="check2-all" className="fs-14 me-2" />
                                    {t('common:buttons.create')}
                                </>,
                                isSubmitting,
                                t('common:buttons.creating'),
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};

export default NewLeadForm;
